import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import { accountSubscriptionApi } from "@/react/api/accountSubscriptionApi";
import { useCurrentPlanApi } from "@circle-react/components/SettingsApp/Plan/hooks/useCurrentPlanApi";
import { useUpgradePlanContext } from "../UpgradePlanContext";
import { UPGRADE_PATHS } from "../constants";

interface UseAccountSubscriptionApiOptions {
  source?: string;
  usedForWorkflows?: boolean;
}

interface UpdateSubscriptionParams {
  planCouponId?: string; // Make this optional if it's not required for every call
  planId: string;
}

export const useAccountSubscriptionApi = ({
  usedForWorkflows = false,
  source,
}: UseAccountSubscriptionApiOptions = {}) => {
  const history = useHistory();
  const { isEmailHubRequired } = useUpgradePlanContext();
  const { refetchCurrentPlan } = useCurrentPlanApi();

  const handleError = () => {
    history.push(UPGRADE_PATHS.ERROR);
  };

  // [TODO][EmailHubBilling][AfterRelease] Rename "updateSubscription" to communityHubUpgrade
  // or something more descriptive as we now have two different subscriptions
  const { mutate: updateSubscription } = useMutation(
    ({ planId, planCouponId }: UpdateSubscriptionParams) =>
      accountSubscriptionApi.update(
        Number(planId),
        planCouponId ? Number(planCouponId) : 0,
        source,
      ),
    {
      onSuccess: () => {
        void refetchCurrentPlan();

        if (isEmailHubRequired) {
          // Its a CH and EH upgrade
          createEmailHubSubscription();
        } else if (usedForWorkflows) {
          // Its a CH only upgrade for workflows
          history.push(UPGRADE_PATHS.WORKFLOW_APP_SYNCING);
        } else {
          // Its a CH only upgrade
          history.push(UPGRADE_PATHS.UPGRADE_SUCCESSFUL);
        }
      },
      onError: handleError,
    },
  );

  const { mutate: createEmailHubSubscription } = useMutation(
    () => accountSubscriptionApi.createEmailHubSubscription(),
    {
      onSuccess: () => {
        // eslint-disable-next-line sonarjs/no-selector-parameter -- Disabled for plugin version update. Fix when possible
        if (usedForWorkflows) {
          history.push(UPGRADE_PATHS.WORKFLOW_APP_SYNCING);
        } else {
          history.push(UPGRADE_PATHS.EMAIL_HUB_SUBSCRIPTION_CHECK);
        }
      },
      onError: handleError,
    },
  );

  return {
    updateSubscription,
    createEmailHubSubscription,
  };
};
